import { RootState } from "@base/store";
import { WithAnalytics } from "@base/utils/hoc/withSendPageAnalytics";
import Header from "@components/molecules/header";
import Layout from "@components/molecules/layout";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import EditProfile from "@components/organisms/edit-profile";
import { ProfileInfo } from "@components/organisms/profile-info";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { homeActions } from "../home/home.slice";
import { EditToggle } from "./molecule/edit-toggle";
import ProfileToggle from "./molecule/profile-toggle";
import { profileActions } from "./profile.slice";
import { CardContainer } from "./profile.styled";
import { BodyProps, UserReviewStats, UserStatsProps } from "./profile.type";

import { ReactComponent as Edit } from "@assets/images/edit/edit.svg";

type ReduxProps = ConnectedProps<typeof connector>;

const Profile = ({
  setUser,
  setLoader,
  setAlert,
  updateUser,
  getUserStats,
  getUserReviewStats,
  uploadProfileImage,
}: ReduxProps) => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [UserStats, setUserStats] = useState<UserStatsProps>({
    games_played: 0,
    games_won: 0,
    hours_played: 0,
  });
  const [UserReviewStats, setUserReviewStats] = useState<null>(null);
  const user = useSelector((state: RootState) => state?.user?.user);
  const currentUser = localStorage.getItem("currentUser");

  const [contentRender, setContentRender] = useState({
    overview: true,
    edit: false,
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      dob: null,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Required"),
      lastName: Yup.string().required("Required"),
      dob: Yup.string().required("Required"),
    }),
    onSubmit: () => {},
  });

  const handleDateChange = (date: Dayjs | null) => {
    if (dayjs(date).isValid()) {
      formik.setFieldValue("dob", date);
    } else {
      formik.setFieldValue("dob", null);
    }
  };

  const getStats = async () => {
    const response = await getUserStats();
    const res = await getUserReviewStats();
    if (response?.meta?.requestStatus === "fulfilled") {
      setUserStats(response?.payload);
    }
    if (res?.meta?.requestStatus === "fulfilled") {
      if (res?.payload?.reviews_count === 0) {
        setUserReviewStats(null);
      } else {
        setUserReviewStats(res?.payload);
      }
    }
  };

  const handlePhoto = (photo: File | null) => {
    setSelectedFile(photo);
  };

  const handleBack = () => {
    if (contentRender?.edit) {
      setContentRender({ overview: true, edit: false });
      formik.resetForm();
    }
    if (contentRender?.overview) {
      navigate("/home");
    }
  };

  const handleSubmit = async () => {
    let body: BodyProps = {
      date_of_birth: "",
      first_name: "",
      last_name: "",
    };
    if (
      formik.values.firstName &&
      formik.values.lastName &&
      formik.values.dob
    ) {
      body = {
        first_name: formik.values.firstName,
        last_name: formik.values.lastName,
        date_of_birth: dayjs(formik.values.dob).format("YYYY-MM-DD"),
      };
    }

    const res = await updateUser(body);
    if (res.meta.requestStatus === "fulfilled") {
      setUser({
        firstName: res?.payload?.first_name,
        lastName: res?.payload?.last_name,
        dob: res?.payload?.date_of_birth,
        isLoading: false,
      });
      if (currentUser) {
        const retrievedUser = JSON.parse(currentUser);
        const newUser = {
          ...retrievedUser,
          firstName: res?.payload?.first_name,
          lastName: res?.payload?.last_name,
          dob: res?.payload?.date_of_birth,
          isLoading: false,
        };
        localStorage.setItem("currentUser", JSON.stringify(newUser));
      }
      setAlert({
        alert: {
          isVisible: true,
          message: `Profile Updated Successfully`,
          type: "success",
        },
      });
      setContentRender({ overview: true, edit: false });
    }
  };

  const uploadProfile = async (formData: any) => {
    const response = await uploadProfileImage(formData);
    setUser({
      profileImg: response?.payload?.profile_url,
    });
    if (currentUser) {
      const retrievedUser = JSON.parse(currentUser);
      const newUser = {
        ...retrievedUser,
        profileImg: response?.payload?.profile_url,
      };
      localStorage.setItem("currentUser", JSON.stringify(newUser));
    }
  };

  useEffect(() => {
    getStats();
  }, []);

  useEffect(() => {
    if (user.firstName) {
      formik.setFieldValue("firstName", user.firstName);
    }
    if (user.lastName) {
      formik.setFieldValue("lastName", user.lastName);
    }
    if (user.dob) {
      handleDateChange(dayjs(user?.dob));
    }
  }, [user]);

  useEffect(() => {
    if (selectedFile) {
      let formData = new FormData();
      formData.append("image", selectedFile);
      uploadProfile(formData);
    }
  }, [selectedFile]);

  return (
    <Layout
      isLayout
      HeaderChildren={
        <Header
          title={"Profile"}
          icon={contentRender.overview ? <Edit /> : undefined}
          handleBack={() => {
            handleBack();
          }}
          handleClick={() => {
            setContentRender({ overview: false, edit: true });
          }}
        />
      }
    >
      <CardContainer>
        {contentRender.overview ? (
          <ProfileToggle />
        ) : (
          <EditToggle handlePhoto={handlePhoto} />
        )}
      </CardContainer>
      {contentRender.overview ? (
        <ProfileInfo stats={UserStats} review={UserReviewStats} />
      ) : (
        <EditProfile
          handleSubmit={handleSubmit}
          handleDateChange={handleDateChange}
          formik={formik}
        />
      )}
    </Layout>
  );
};
const mapDispatchToProps = {
  setLoader: LoaderSnackbarWrapperActions.setGlobalAppearances,
  setAlert: LoaderSnackbarWrapperActions.setGlobalAppearances,
  setUser: homeActions.setUser,
  updateUser: profileActions.updateUser,
  getUserStats: profileActions.getUserStats,
  getUserReviewStats: profileActions.getUserReviewStats,
  uploadProfileImage: profileActions.uploadProfileImage,
};

const connector = connect(undefined, mapDispatchToProps);
const ProfileRedux = connector(WithAnalytics(Profile));

export { ProfileRedux as Profile };
